import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import i18n from '@/i18n';

const LANGS = [
  { code: 'tr', label: 'TR', flag: '🇹🇷' },
  { code: 'en', label: 'EN', flag: '🇬🇧' },
  { code: 'ar', label: 'AR', flag: '🇸🇦' },
];

export default function Navbar() {
  const { t } = useTranslation();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const [currentLang, setCurrentLang] = useState(i18n.language || 'tr');

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', fn, { passive: true });
    fn();
    return () => window.removeEventListener('scroll', fn);
  }, []);

  useEffect(() => { setMenuOpen(false); setLangOpen(false); }, [location.pathname]);

  const switchLang = (code: string) => {
    i18n.changeLanguage(code);
    setCurrentLang(code);
    setLangOpen(false);
    document.documentElement.dir = code === 'ar' ? 'rtl' : 'ltr';
    document.documentElement.lang = code;
  };

  const links = [
    { label: t('nav.home'), to: '/' },
    { label: t('nav.services'), to: '/hizmetler' },
    { label: t('nav.about'), to: '/bizim' },
    { label: t('nav.booking'), to: '/randevu' },
  ];

  const bg = scrolled || menuOpen;
  const activeLang = LANGS.find((l) => l.code === currentLang) || LANGS[0];

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${bg ? 'bg-white border-b border-gray-100' : 'bg-transparent'}`}>
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="flex items-center justify-between h-16 md:h-20">

          {/* Logo */}
          <Link to="/" className="flex items-center gap-2.5 cursor-pointer shrink-0">
            <div className="w-9 h-9 md:w-11 md:h-11 rounded-full overflow-hidden bg-white border-2 border-blue-100 shrink-0">
              <img
                src="https://static.readdy.ai/image/c7416925641f36c4b72e81cd7ba1ed46/1ae22aa63f51b461cd10a9da4895730b.jpeg"
                alt="Vanpolat Tesisat"
                className="w-full h-full object-cover"
              />
            </div>
            <div>
              <p className={`font-extrabold text-sm md:text-base leading-none ${bg ? 'text-blue-800' : 'text-white'}`} style={{ fontFamily: 'Poppins, sans-serif' }}>
                Vanpolat
              </p>
              <p className={`text-xs leading-none mt-0.5 ${bg ? 'text-blue-500' : 'text-blue-200'}`}>Tesisat</p>
            </div>
          </Link>

          {/* Desktop links */}
          <div className="hidden md:flex items-center gap-7">
            {links.map((l) => (
              <Link key={l.to} to={l.to}
                className={`text-sm font-semibold transition-colors whitespace-nowrap cursor-pointer ${
                  location.pathname === l.to
                    ? bg ? 'text-blue-600' : 'text-white font-bold'
                    : bg ? 'text-gray-700 hover:text-blue-600' : 'text-white/85 hover:text-white'
                }`}>
                {l.label}
              </Link>
            ))}
          </div>

          {/* Desktop CTA + Lang */}
          <div className="hidden md:flex items-center gap-2">
            <a href="tel:+905076074165"
              className="flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-bold px-4 py-2 rounded-lg transition-colors cursor-pointer whitespace-nowrap">
              <i className="ri-phone-fill"></i> {t('nav.call')}
            </a>
            <a href="https://wa.me/905076074165?text=Merhaba%2C%20tesisat%20konusunda%20yard%C4%B1m%20istiyorum."
              target="_blank" rel="nofollow noreferrer"
              className="flex items-center gap-1.5 bg-green-500 hover:bg-green-600 text-white text-sm font-bold px-4 py-2 rounded-lg transition-colors cursor-pointer whitespace-nowrap">
              <i className="ri-whatsapp-line"></i> WA
            </a>
            {/* Lang */}
            <div className="relative">
              <button onClick={() => setLangOpen(!langOpen)}
                className={`flex items-center gap-1 text-xs font-bold px-3 py-2 rounded-lg border transition-all cursor-pointer whitespace-nowrap ${
                  bg ? 'border-gray-200 text-gray-700 bg-gray-50 hover:bg-gray-100' : 'border-white/30 text-white bg-white/10 hover:bg-white/20'
                }`}>
                {activeLang.flag} {activeLang.label}
                <i className={`ri-arrow-down-s-line text-xs transition-transform ${langOpen ? 'rotate-180' : ''}`}></i>
              </button>
              {langOpen && (
                <div className="absolute right-0 top-full mt-1 bg-white border border-gray-100 rounded-xl overflow-hidden min-w-[130px] z-50">
                  {LANGS.map((lang) => (
                    <button key={lang.code} onClick={() => switchLang(lang.code)}
                      className={`w-full flex items-center gap-2 px-3 py-2.5 text-xs hover:bg-blue-50 cursor-pointer text-left whitespace-nowrap transition-colors ${
                        currentLang === lang.code ? 'bg-blue-50 text-blue-700 font-bold' : 'text-gray-700'
                      }`}>
                      {lang.flag} {lang.label}
                      {currentLang === lang.code && <i className="ri-check-line text-blue-600 ml-auto"></i>}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Mobile right */}
          <div className="md:hidden flex items-center gap-2">
            {/* Call button on mobile navbar */}
            <a href="tel:+905076074165"
              className="flex items-center gap-1 bg-blue-600 text-white text-xs font-bold px-3 py-2 rounded-lg cursor-pointer whitespace-nowrap">
              <i className="ri-phone-fill text-xs"></i> Ara
            </a>
            {/* Lang */}
            <div className="relative">
              <button onClick={() => { setLangOpen(!langOpen); setMenuOpen(false); }}
                className={`flex items-center gap-1 text-xs font-bold px-2 py-2 rounded-lg border cursor-pointer whitespace-nowrap ${
                  bg ? 'border-gray-200 text-gray-700 bg-gray-50' : 'border-white/30 text-white bg-white/10'
                }`}>
                {activeLang.flag}
              </button>
              {langOpen && (
                <div className="absolute right-0 top-full mt-1 bg-white border border-gray-100 rounded-xl overflow-hidden min-w-[110px] z-50">
                  {LANGS.map((lang) => (
                    <button key={lang.code} onClick={() => switchLang(lang.code)}
                      className={`w-full flex items-center gap-2 px-3 py-2.5 text-xs hover:bg-blue-50 cursor-pointer text-left whitespace-nowrap ${
                        currentLang === lang.code ? 'bg-blue-50 text-blue-700 font-bold' : 'text-gray-700'
                      }`}>
                      {lang.flag} {lang.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
            {/* Hamburger */}
            <button onClick={() => { setMenuOpen(!menuOpen); setLangOpen(false); }}
              className={`w-9 h-9 flex items-center justify-center rounded-lg cursor-pointer ${bg ? 'text-gray-700' : 'text-white'}`}
              aria-label="Menü">
              <i className={`text-xl ${menuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {menuOpen && (
        <div className="md:hidden bg-white border-t border-gray-100 px-4 pb-4">
          <div className="flex flex-col gap-1 pt-2">
            {links.map((l) => (
              <Link key={l.to} to={l.to}
                className={`px-4 py-3 rounded-xl text-sm font-semibold cursor-pointer transition-colors ${
                  location.pathname === l.to ? 'bg-blue-50 text-blue-700' : 'text-gray-700 hover:bg-gray-50'
                }`}>
                {l.label}
              </Link>
            ))}
          </div>
          <div className="flex gap-2 mt-3">
            <a href="tel:+905076074165"
              className="flex-1 flex items-center justify-center gap-2 bg-blue-600 text-white text-sm font-bold py-3 rounded-xl cursor-pointer whitespace-nowrap">
              <i className="ri-phone-fill"></i> {t('nav.call')}
            </a>
            <a href="https://wa.me/905076074165?text=Merhaba%2C%20tesisat%20konusunda%20yard%C4%B1m%20istiyorum."
              target="_blank" rel="nofollow noreferrer"
              className="flex-1 flex items-center justify-center gap-2 bg-green-500 text-white text-sm font-bold py-3 rounded-xl cursor-pointer whitespace-nowrap">
              <i className="ri-whatsapp-line"></i> WhatsApp
            </a>
          </div>
        </div>
      )}
    </nav>
  );
}
